import { defineXray } from "../src/xray/manifest.js";
import { SHELL_TOOL_NAMES } from "../src/mine/shell.js";
import { extractSectionOps, describeMissing } from "../extractors/sections/index.js";
import { collect, build, groupBy, anchor, ignore } from "../extractors/sections/spec.js";

export default defineXray({
  id: "sections",
  label: "Batched",
  title: "Calls that asked several questions at once — the delimiters the agent wrote into its own "
    + "output, what came back under each, and which questions it never reached",

  consumer: "someone reading a long scripted call and trying to find the part that answered their question",
  decision: "which of the questions a batched call asked actually got answered, and which the result never reached",

  // TRACE-ONLY. The program being scripted is not a target in the world — two agents
  // scripting cdb are not acting on the same thing — and the content is the question
  // set, which is per-session. Read inline it is the structure of one enormous
  // result; aggregated it is a tally of tools people script.
  tab: false,
  tabReason: "the unit is one call's internal structure, which means nothing aggregated across sessions",

  // Trace-only, so this does not down-select a tab of its own — it is how the
  // LINEAGE window finds the prompts carrying this lens's work. Same matcher
  // its tab-bearing siblings use: every op here is read off a shell command.
  substrate: { label: "shell (scripted programs)", match: (n) => SHELL_TOOL_NAMES.test(String(n).split("__").pop()) },

  feed: { domain: "sections" },
  extract: extractSectionOps, collect, group: build, groupBy, anchor, ignore,
  // No ref: the thing scripted is a program, not a target, so there is no identity
  // to cite. It still belongs in the graph — the QUESTIONS a batched call asked are
  // prose, and the entity and shingle tiers exist for exactly this, a node that can
  // be matched on what it says rather than on what it names.
  //
  // This extractor sets `output: null` unconditionally (the result is one blob for
  // the whole call and no section can claim a line of it), so the markers are all
  // the text there is.
  lineage: {
    kind: "batched",
    ref: () => null,
    role: () => "consultation",
    text: (op) => [...(op.sections || []).map((s) => s.marker), op.output].filter(Boolean).join("\n"),
    label: (op) => `asked ${op.asked} of ${op.program} at once`,
    intent: (op) => (op.lost ? `${op.lost} never reached` : null),
  },

  emptyNote: "No batched multi-question calls in this window",
  explainMissing: describeMissing,
});
